import { GameState, Player } from '../../../domain/entities';

/**
 * FitnessProcessor - Handles player fitness updates
 *
 * Responsibilities:
 * - Reduce fitness for players who played matches
 * - Factor in High Press tactic for additional fitness cost
 * - Recover fitness for rested players
 * - Apply age-related recovery penalties
 */
export class FitnessProcessor {
    /**
     * Process fitness updates for all players
     */
    static processFitnessUpdates(
        gameState: GameState,
        playersWhoPlayed: Set<number>
    ): void {
        Object.values(gameState.leagues).forEach(league => {
            league.teams.forEach(team => {
                team.squad.forEach(player => {
                    if (player.injury) return; // No fitness changes for injured players

                    if (playersWhoPlayed.has(player.id)) {
                        this.depleteFitness(player, team.tactics.defensiveStyle === 'High Press');
                    } else {
                        this.recoverFitness(player);
                    }

                    player.fitness = Math.round(player.fitness);
                });
            });
        });
    }

    /**
     * Reduce fitness for a player who participated in a match
     * Uses logarithmic scaling: high fitness loses less, low fitness loses slightly less but modifier gets worse
     */
    private static depleteFitness(player: Player, isHighPress: boolean): void {
        // Logarithmic fitness loss based on current fitness level
        // At 100 fitness: loses ~4 points
        // At 50 fitness: loses ~5 points
        // At 20 fitness: loses ~6 points
        // Formula: 3 + ln(fitness) where fitness ranges 1-100
        const baseMultiplier = player.fitnessMax >= 95 ? 1.0 : player.fitnessMax <= 80 ? 1.4 : 1.2;
        const fitnessDrop = (3 + Math.log(Math.max(1, player.fitness))) * baseMultiplier;

        // High Press is 20% more physically demanding
        let adjustedDrop = fitnessDrop;
        if (isHighPress) {
            adjustedDrop *= 1.2;
        }

        player.fitness = Math.max(0, player.fitness - adjustedDrop);
    }

    /**
     * Recover fitness for a rested player
     */
    private static recoverFitness(player: Player): void {
        // Base recovery from fitnessRecovery stat
        let recoveryAmount = player.fitnessRecovery;

        // Recovery slows after age 28 (-0.5 per year)
        if (player.age > 28) {
            recoveryAmount -= (player.age - 28) * 0.5;
        }

        // Ensure non-negative recovery
        recoveryAmount = Math.max(0, recoveryAmount);

        player.fitness = Math.min(100, player.fitness + recoveryAmount);
    }
}
